"use client";
import { useRef } from "react";
import { useRouter } from "next/navigation";
import { FaArrowRight } from "react-icons/fa6";

export default function ServiceCard({ icon, title, description, accent = "#3B5BDB", index = 0, link }) {
  const cardRef = useRef(null);
  const router = useRouter();

  const handleMouseMove = (e) => {
    const card = cardRef.current;
    if (!card) return;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const cx = rect.width / 2;
    const cy = rect.height / 2;
    const rotateX = ((y - cy) / cy) * -6;
    const rotateY = ((x - cx) / cx) * 6;
    card.style.transform = `perspective(800px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateY(-4px) scale(1.015)`;

    const spotlight = card.querySelector(".card-spotlight");
    if (spotlight) {
      spotlight.style.left = `${x}px`;
      spotlight.style.top = `${y}px`;
      spotlight.style.opacity = "1";
    }
  };

  const handleMouseLeave = () => {
    const card = cardRef.current;
    if (!card) return;
    card.style.transform = "";
    const spotlight = card.querySelector(".card-spotlight");
    if (spotlight) spotlight.style.opacity = "0";
  };

  const brandRgb = "59,91,219";
  const accentRgb = "0,180,216";

  return (
    <div
      ref={cardRef}
      className="service-card relative rounded-[20px] transition-all duration-300 will-change-transform cursor-default"
      data-index={index}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{
        transition: "transform 0.3s cubic-bezier(0.16,1,0.3,1), box-shadow 0.3s ease",
      }}
    >
      <div
        className="absolute inset-0 rounded-[20px] !p-[1.5px] pointer-events-none z-10"
        style={{
          background: `linear-gradient(135deg,
            rgba(${brandRgb},0.55) 0%,
            rgba(${accentRgb},0.25) 40%,
            rgba(${brandRgb},0.08) 70%,
            rgba(${accentRgb},0.35) 100%
          )`,
          WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
          WebkitMaskComposite: "xor",
          maskComposite: "exclude",
        }}
      />

      <div
        className="relative h-full rounded-[20px] overflow-hidden backdrop-blur-[20px] saturate-150"
        style={{
          background: `linear-gradient(
            145deg,
            rgba(255,255,255,0.06) 0%,
            rgba(${brandRgb},0.04) 50%,
            rgba(${accentRgb},0.03) 100%
          )`,
          boxShadow: `
            inset 0 1px 0 rgba(255,255,255,0.1),
            inset 0 -1px 0 rgba(0,0,0,0.15),
            0 8px 32px rgba(0,0,0,0.25),
            0 2px 8px rgba(0,0,0,0.15)
          `,
          padding: "28px 24px 24px",
        }}
      >
        <div
          className="card-spotlight absolute w-[220px] h-[220px] rounded-full pointer-events-none opacity-0 transition-opacity duration-300 -translate-x-1/2 -translate-y-1/2 z-0"
          style={{
            background: `radial-gradient(circle, rgba(${brandRgb},0.18) 0%, rgba(${accentRgb},0.08) 40%, transparent 70%)`,
          }}
        />

        <div
          className="absolute top-0 left-[10%] right-[10%] h-[1px]"
          style={{
            background: `linear-gradient(90deg, transparent, rgba(${brandRgb},0.8) 30%, rgba(${accentRgb},0.9) 70%, transparent)`,
            filter: "blur(0.5px)",
          }}
        />

        <div className="service-card__icon relative z-10 w-[54px] h-[54px] rounded-[14px] flex items-center justify-center text-2xl !mb-[18px]">
          {icon}
          <div
            className="absolute inset-[-3px] rounded-[17px]"
            style={{
              background: `radial-gradient(circle, rgba(${brandRgb},0.15), transparent 70%)`,
              filter: "blur(4px)",
            }}
          />
        </div>

        <div className="absolute top-5 right-5 z-10 font-mono text-[10px] font-bold tracking-[0.1em] !px-2 !py-0.5 rounded-md">{String(index + 1).padStart(2, "0")}</div>

        <h3 className="relative z-10 font-display font-bold text-[15.5px] leading-tight tracking-[-0.02em] !mb-2.5 text-text-primary">{title}</h3>

        <p className="relative z-10 text-[13px] leading-[1.75] !mb-5 text-text-secondary">{description}</p>

        <div className="relative z-10 flex items-center justify-between !pt-4 border-t" style={{ borderColor: `rgba(${brandRgb},0.15)` }}>
          <span className="text-[11px] font-bold tracking-[0.08em] uppercase text-transparent bg-clip-text bg-gradient-to-l from-cyan-400 to-purple-600">Learn more</span>

          <button
            onClick={() => router.push(link)}
            aria-label={`Learn more about ${title}`}
            className="w-8 h-8 rounded-full flex items-center justify-center border transition-all duration-200 hover:scale-110 active:scale-95 cursor-pointer"
            style={{
              background: `linear-gradient(135deg, rgba(${brandRgb},0.25), rgba(${accentRgb},0.15))`,
              borderColor: `rgba(${brandRgb},0.4)`,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = `linear-gradient(135deg, rgba(${brandRgb},0.5), rgba(${accentRgb},0.35))`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = `linear-gradient(135deg, rgba(${brandRgb},0.25), rgba(${accentRgb},0.15))`;
            }}
          >
            <FaArrowRight size={13} color="var(--text-primary)" aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
}
